/**
 * CcSessionDescriptionHandler: the stock sip.js web SDH plus `setCodecPreferences` on every
 * audio transceiver in the tenant's policy order, applied right before each offer or answer is
 * created (so both the offer and the answer paths get the ordering).
 */
import { Web, type Session } from 'sip.js';
import { applyCodecPreferences } from './codec-prefs.js';

export class CcSessionDescriptionHandler extends Web.SessionDescriptionHandler {
  constructor(
    logger: ConstructorParameters<typeof Web.SessionDescriptionHandler>[0],
    mediaStreamFactory: Web.MediaStreamFactory,
    configuration: Web.SessionDescriptionHandlerConfiguration | undefined,
    private readonly codecs: readonly string[],
  ) {
    super(logger, mediaStreamFactory, configuration);
  }

  protected override createLocalOfferOrAnswer(options?: Web.SessionDescriptionHandlerOptions): Promise<RTCSessionDescriptionInit> {
    const pc = this.peerConnection;
    if (pc) applyCodecPreferences(pc, this.codecs);
    return super.createLocalOfferOrAnswer(options);
  }
}

export const DEFAULT_ICE_GATHERING_TIMEOUT_MS = 1500;

/**
 * Same merge as sip.js' defaultSessionDescriptionHandlerFactory (iceGatheringTimeout and
 * peerConnectionConfiguration from `sessionDescriptionHandlerFactoryOptions`), constructing
 * the Cc subclass. `mediaStreamFactory` is injectable so the device layer can pick the mic.
 */
export function ccSessionDescriptionHandlerFactory(codecs: readonly string[], mediaStreamFactory?: Web.MediaStreamFactory): Web.SessionDescriptionHandlerFactory {
  return (session: Session, options?: Web.SessionDescriptionHandlerFactoryOptions): Web.SessionDescriptionHandler => {
    const msf = mediaStreamFactory ?? Web.defaultMediaStreamFactory();
    const iceGatheringTimeout = options?.iceGatheringTimeout !== undefined ? options.iceGatheringTimeout : DEFAULT_ICE_GATHERING_TIMEOUT_MS;
    const configuration: Web.SessionDescriptionHandlerConfiguration = {
      iceGatheringTimeout,
      peerConnectionConfiguration: { ...Web.defaultPeerConnectionConfiguration(), ...options?.peerConnectionConfiguration },
    };
    const logger = session.userAgent.getLogger('sip.SessionDescriptionHandler');
    return new CcSessionDescriptionHandler(logger, msf, configuration, codecs);
  };
}
